import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Navigation, Loader2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const FALLBACK: [number, number] = [-19.92, -43.94];

function MapRecenter({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [center, zoom, map]);
  return null;
}

function buildAddressQuery(parts: {
  cep: string;
  endereco: string;
  numero?: string;
  bairro: string;
  cidade: string;
  uf: string;
}) {
  const logradouroComNumero = parts.numero && parts.endereco ? `${parts.endereco}, ${parts.numero}` : parts.endereco;
  const chunks = [
    logradouroComNumero,
    parts.bairro,
    parts.cidade,
    parts.uf,
    parts.cep?.replace(/\D/g, '').length === 8 ? parts.cep.replace(/\D/g, '') : '',
    'Brasil',
  ].filter(Boolean);
  return chunks.join(', ');
}

async function geocodeAddress(query: string): Promise<[number, number] | null> {
  if (query.length < 8) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`;
    const resp = await fetch(url, { headers: { 'User-Agent': 'AlunoConectado/1.0' } });
    const data = await resp.json();
    if (data?.[0]?.lat && data?.[0]?.lon) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
  } catch { /* ignore */ }
  return null;
}

/** Mapa de pré-visualização do endereço (CEP + logradouro) no wizard do aluno */
export function AlunoEnderecoMap({
  cep,
  endereco,
  numero,
  bairro,
  cidade,
  uf,
  latitude,
  longitude,
}: {
  cep: string;
  endereco: string;
  numero?: string;
  bairro: string;
  cidade: string;
  uf: string;
  latitude?: number | null;
  longitude?: number | null;
}) {
  const [coords, setCoords] = useState<[number, number] | null>(
    latitude != null && longitude != null ? [latitude, longitude] : null,
  );
  const [loading, setLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const query = useMemo(
    () => buildAddressQuery({ cep, endereco, numero, bairro, cidade, uf }),
    [cep, endereco, bairro, cidade, uf],
  );

  const canSearch = query.replace(/, Brasil$/, '').length >= 6;

  useEffect(() => {
    if (latitude != null && longitude != null) {
      setCoords([latitude, longitude]);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (!canSearch) {
      if (latitude == null || longitude == null) setCoords(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      setGeoError(null);
      const found = await geocodeAddress(query);
      if (found) {
        setCoords(found);
      } else if (latitude == null || longitude == null) {
        setCoords(null);
        setGeoError('Não foi possível localizar este endereço no mapa.');
      }
      setLoading(false);
    }, 900);
    return () => clearTimeout(t);
  }, [query, canSearch, latitude, longitude]);

  const handleMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocalização não disponível neste navegador.');
      return;
    }
    setLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords([pos.coords.latitude, pos.coords.longitude]);
        setLoading(false);
      },
      () => {
        setGeoError('Permissão de localização negada ou indisponível.');
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const center = coords ?? FALLBACK;
  const zoom = coords ? 16 : 11;

  return (
    <div className="ac-aluno-endereco-map">
      <div className="ac-aluno-endereco-map-head">
        <span className="ac-aluno-endereco-map-title">
          <MapPin size={13} strokeWidth={1.5} />
          Localização
        </span>
        <button
          type="button"
          className="ac-aluno-endereco-map-geo-btn"
          onClick={handleMyLocation}
          disabled={loading}
          title="Usar localização atual do dispositivo"
        >
          {loading ? <Loader2 size={12} className="ac-spin" /> : <Navigation size={12} />}
          Minha posição
        </button>
      </div>
      <div className="ac-aluno-endereco-map-frame">
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom={false}
          className="ac-aluno-endereco-map-leaflet"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <MapRecenter center={center} zoom={zoom} />
          {coords && (
            <Marker position={coords}>
              <Popup>
                {endereco || bairro || cidade
                  ? `${endereco || ''}${bairro ? `, ${bairro}` : ''}${cidade ? ` — ${cidade}/${uf}` : ''}`
                  : 'Endereço do aluno'}
              </Popup>
            </Marker>
          )}
        </MapContainer>
        {!coords && !loading && (
          <div className="ac-aluno-endereco-map-placeholder">
            Preencha CEP ou endereço para ver o mapa
          </div>
        )}
        {loading && (
          <div className="ac-aluno-endereco-map-loading">
            <Loader2 size={18} className="ac-spin" />
          </div>
        )}
      </div>
      {geoError && <p className="ac-aluno-endereco-map-notice">{geoError}</p>}
      <p className="ac-aluno-endereco-map-hint">
        O pin é estimado pelo endereço. Ao salvar, o CEP atualiza as coordenadas no cadastro.
      </p>
    </div>
  );
}
